import React, { createContext, useState, useContext, useEffect } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      // Common website translations
      navbar: {
        home: 'Home',
        about: 'About',
        services: 'Services',
        careers: 'Careers',
        contact: 'Contact'
      },
      footer: {
        copyright: '© 2024 WTT International. All Rights Reserved.',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service'
      },
      careers: {
        joinTeam: 'Join Our Team',
        openPositions: 'Open Positions',
        noPositions: 'No open positions at the moment.'
      },
      newSection: {
        title: 'New Title',
        description: 'New description'
      }
    }
  },
  es: {
    translation: {
      navbar: {
        home: 'Inicio',
        about: 'Acerca de',
        services: 'Servicios',
        careers: 'Carreras',
        contact: 'Contacto'
      },
      footer: {
        copyright: '© 2024 WTT International. Todos los derechos reservados.',
        privacyPolicy: 'Política de Privacidad',
        termsOfService: 'Términos de Servicio'
      },
      careers: {
        joinTeam: 'Únete a Nuestro Equipo',
        openPositions: 'Posiciones Abiertas',
        noPositions: 'No hay posiciones abiertas en este momento.'
      },
      newSection: {
        title: 'Nuevo Título',
        description: 'Nueva descripción'
      }
    }
  },
  fr: {
    translation: {
      navbar: {
        home: 'Accueil',
        about: 'À propos',
        services: 'Services',
        careers: 'Carrières',
        contact: 'Contact'
      },
      footer: {
        copyright: '© 2024 WTT International. Tous droits réservés.',
        privacyPolicy: 'Politique de Confidentialité',
        termsOfService: 'Conditions de Service'
      },
      careers: {
        joinTeam: 'Rejoignez Notre Équipe',
        openPositions: 'Postes Ouverts',
        noPositions: 'Aucun poste ouvert pour le moment.'
      }
    }
  }
};

// Configure i18next
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en', // default language
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false // react already escapes values
    }
  });

// Language Context
const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(
    localStorage.getItem('appLanguage') || 'en'
  );

  useEffect(() => {
    i18n.changeLanguage(language);
    localStorage.setItem('appLanguage', language);
    document.documentElement.lang = language;
    document.documentElement.dir = 
      ['ar', 'he'].includes(language) ? 'rtl' : 'ltr';
  }, [language]);

  const changeLanguage = (lng) => {
    setLanguage(lng);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default i18n; 